/**
 * <scale-marquee> — scales each tile of a marquee by how close it is to the
 * horizontal center of the element.
 *
 * The infinite scroll itself is handled by the theme's <marquee-element>, which
 * clones its single child to fill the row and drives it with a CSS animation.
 * This element only reads positions and writes transforms, so the two can run
 * side by side without fighting over the same properties.
 */
class ScaleMarquee extends HTMLElement {
  constructor() {
    super();

    this.items = [];
    this.trackedChildCount = 0;
    this.frame = null;
    this.running = false;
    this.onTick = this.onTick.bind(this);
  }

  connectedCallback() {
    if (this.hasAttribute('data-scale-disabled') || this.motionReduced) return;

    // Guard against a second observer if the theme editor re-attaches the node.
    this.observer?.disconnect();
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => (entry.isIntersecting ? this.start() : this.stop()));
      },
      { rootMargin: '100px 0px 100px 0px' }
    );
    this.observer.observe(this);
  }

  disconnectedCallback() {
    this.stop();
    this.observer?.disconnect();
  }

  get motionReduced() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  get minScale() {
    const value = parseFloat(this.getAttribute('data-min-scale'));
    return Number.isFinite(value) ? Math.min(Math.max(value, 0.1), 1) : 0.78;
  }

  get track() {
    return this.querySelector('marquee-element') || this;
  }

  start() {
    if (this.running) return;
    this.running = true;
    this.setAttribute('data-scale-active', '');
    this.frame = requestAnimationFrame(this.onTick);
  }

  stop() {
    if (!this.running) return;
    this.running = false;
    this.removeAttribute('data-scale-active');
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = null;
  }

  /**
   * <marquee-element> appends clones after this element is connected, so the
   * item list is refreshed whenever its child count changes.
   */
  refreshItems() {
    const childCount = this.track.childElementCount;
    if (childCount === this.trackedChildCount && this.items.length) return;

    this.trackedChildCount = childCount;
    this.items = Array.from(this.querySelectorAll('.media-marquee__item'));
  }

  onTick() {
    if (!this.running) return;

    this.refreshItems();

    const bounds = this.getBoundingClientRect();
    const center = bounds.left + bounds.width / 2;
    const width = this.items.length ? this.items[0].offsetWidth : 0;

    if (bounds.width > 0 && width > 0) {
      const group = this.items[0].parentElement;
      const gap = parseFloat(getComputedStyle(group).columnGap) || 0;
      const pitch = width + gap;
      // Half the element, but never less than a tile and a half, so narrow
      // screens get a gentle falloff instead of a cliff between two tiles.
      const reach = Math.max(bounds.width / 2, pitch * 1.5);

      // scale(x) = flat + amplitude * cos(k * x) inside `reach`, then flat min.
      const min = this.minScale;
      const amplitude = (1 - min) / 2;
      const flat = min + amplitude;
      const k = Math.PI / reach;

      // Scaling alone shrinks the tiles but not the pitch, so the visible gap
      // grows toward the edges. Sliding each tile back toward the center by the
      // scale deficit accumulated between it and the center keeps every gap
      // equal to `gap`. `area` is the accumulated scale; its coefficient is the
      // trapezoid-consistent one rather than the plain integral (wave / k) so
      // the gap is exact for tiles a whole pitch apart, not just in the limit.
      const half = (k * pitch) / 2;
      const sine = Math.sin(half);
      const coefficient = Math.abs(sine) > 1e-6 ? (amplitude * pitch * Math.cos(half)) / (2 * sine) : amplitude / k;
      const span = flat * reach;

      // Read every position first, then write every transform, so the loop
      // doesn't interleave layout reads and writes. The measured box already
      // carries last frame's translate; scaling keeps a box centered, so
      // subtracting it recovers the untranslated layout position.
      const offsets = this.items.map((item) => {
        const rect = item.getBoundingClientRect();
        return rect.left + rect.width / 2 - center - (item.scaleMarqueeShift || 0);
      });

      const transforms = offsets.map((offset) => {
        const distance = Math.abs(offset);
        const ratio = Math.min(distance / reach, 1);
        // Cosine falloff: flat near the center, gentle at the edges.
        const falloff = (1 - Math.cos((1 - ratio) * Math.PI)) / 2;
        const scale = min + (1 - min) * falloff;
        const area =
          distance <= reach
            ? flat * distance + coefficient * Math.sin(k * distance)
            : span + min * (distance - reach);
        const placed = (gap * distance + width * area) / pitch;

        return { scale, shift: Math.sign(offset) * placed - offset };
      });

      this.items.forEach((item, index) => {
        const { scale, shift } = transforms[index];
        item.scaleMarqueeShift = shift;
        item.style.transform = `translateX(${shift.toFixed(2)}px) scale(${scale.toFixed(4)})`;
      });
    }

    this.frame = requestAnimationFrame(this.onTick);
  }
}

if (!customElements.get('scale-marquee')) {
  customElements.define('scale-marquee', ScaleMarquee);
}
